import _get from 'lodash/get'
import _isFinite from 'lodash/isFinite'
import _size from 'lodash/size'
import { map as mapPromise, all as allPromise } from 'bluebird'
import { get as apiGet, TYPE_BROOKLYN } from 'api-client'
import { get as getConfig } from 'config'
import { setMultipleSeriesProcessing as setMultipleSeriesProcessingAction } from 'services/series/actions'
import { getSeriesMultiple } from 'services/series/middleware'
import { setMultipleVideosProcessing as setMultipleVideosProcessingAction } from 'services/videos/actions'
import { getVideoMultiple } from 'services/videos/middleware'
import { isTypeSeriesAll, isTypeVideoAll, isTypeEpisodeAll } from 'services/content-type'
import { setEventSeriesImpressed, setEventVideoImpressed } from 'services/event-tracking/actions'
import { Map, List, Set, fromJS } from 'immutable'
import { SCREEN_TYPE_MEMBER_HOME } from 'services/upstream-context'
import {
  PM_PLACEMENT_PAYLOAD_TYPE_FEATURED_CONTENT_IMPRESSION,
  PM_PLACEMENT_PAYLOAD_TYPE_PINE_TOP_VIDEO,
  PM_PLACEMENT_PAYLOAD_TYPE_SPOTLIGHT_LIVE_ACCESS,
} from 'services/pm-placement/payload-types'
import {
  getPmPlacement as getPmPlacementAction,
  setPmPlacement as setPmPlacementAction,
  setPmPlacementProcessing as setPmPlacementProcessingAction,
  setPmPlacementError as setPmPlacementErrorAction,
} from './actions'

const config = getConfig()

// Code must be compatible with all features up to the given version:
export const API_VERSION = '1.252.1'

/**
 * Get PM Placement singleton to limit the amount
 * of timeouts to 1
 */
let _getPmPlacementTimeout

/**
 * Get multiple page manger placements and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String[]|List|Set} options.placementNames The page manager placement names
 * @param {String} options.sectionId The page manager section id
 * @param {String} options.language The request language
 * @param {Boolean} [options.LASpotlightOption] When true enable live access spotlight
 * @param {Boolean} [options.disableImpressionCap] When true disable the merchandising
 * impression cap
 * @param {Map} options.auth The auth state
 * @param {Map} options.app The app state
 * @param {Map} options.page The page state
 * @param {Map} options.videos The videos state
 * @param {Map} options.pmPlacement The page manager placement state
 */
export async function getPmPlacementMultiple (options = {}) {
  const {
    dispatch,
    sectionId,
    language,
    auth = Map(),
    app = Map(),
    page = Map(),
    videos = Map(),
    pmPlacement = Map(),
    LASpotlightOption,
    disableImpressionCap,
  } = options
  let { placementNames = [] } = options
  if (List.isList(placementNames) || Set.isSet(placementNames)) {
    placementNames = placementNames.toArray()
  }
  try {
    await mapPromise(placementNames, placementName => getPmPlacementSingle({
      dispatch,
      placementName,
      sectionId,
      language,
      auth,
      app,
      page,
      videos,
      pmPlacement,
      LASpotlightOption,
      disableImpressionCap,
    }))
  } catch (e) {
    // Do nothing
  }
}

/**
 * Get one page manger placements and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.placementName The page manager placement name
 * @param {String} options.sectionId The page manager section id
 * @param {String} options.language The request language
 * @param {Boolean} [options.LASpotlightOption=true] When true enable live access spotlight
 * @param {Boolean} [options.disableImpressionCap] When true disable the merchandising
 * impression cap
 * @param {Map} options.auth The auth state
 * @param {Map} options.app The app state
 * @param {Map} options.page The page state
 * @param {Map} options.videos The videos state
 * @param {Map} options.pmPlacement The page manager placement state
 */
export async function getPmPlacementSingle (options = {}) {
  const {
    dispatch,
    placementName,
    sectionId,
    language,
    auth = Map(),
    app = Map(),
    page = Map(),
    videos = Map(),
    pmPlacement = Map(),
    LASpotlightOption = true,
    disableImpressionCap = _get(config, 'features.disableImpressionCap'),
  } = options
  try {
    const processing = pmPlacement.getIn([placementName, language, 'processing'])
    if (processing) {
      return
    }
    dispatch(setPmPlacementProcessingAction(placementName, language, true))
    const response = await apiGet(
      `placement-content/${placementName}`,
      {
        apiVersion: API_VERSION,
        language,
        disableImpressionCap: disableImpressionCap ? 1 : undefined,
        LASpotlightOption,
      },
      { auth },
      TYPE_BROOKLYN,
    )
    const pmPlacementData = fromJS(_get(response, 'body', {}))
    const isMerch = pmPlacementData.get('payloadType') === 'merchandising-content'
    dispatch(setPmPlacementAction(placementName, language, pmPlacementData))
    const payload = pmPlacementData.get('payload') || Map()
    if (payload) {
      const contentId = isMerch ?
        payload.getIn(['contentItems', 0, 'contentId']) :
        payload.get('id')

      const contentType = isMerch ?
        payload.getIn(['contentItems', 0, 'contentType']) :
        payload.get('type')

      const impressed = videos.getIn([contentId, language, 'impressed'])
      const isVideo = isTypeVideoAll(contentType)
      const isEpisode = isTypeEpisodeAll(contentType)
      const isSeries = isTypeSeriesAll(contentType)
      const videoEvent = isVideo || isEpisode
      const typeId = isSeries ? 'seriesId' : 'videoId'
      const upstreamContext = Map({
        contentId,
        contentType,
        contextType: 'pm/spotlight',
        merchEventId: pmPlacementData.get('merchEventId'),
        score: payload.get('pineScore', null),
        screenType: placementName === 'member-home-spotlight-a-v1' ? SCREEN_TYPE_MEMBER_HOME : null,
        sectionId,
        sectionIndex: 0,
        source: payload.get('pineSource', null),
        typeId: contentId,
      })
      const eventOptions = {
        auth,
        location,
        page,
        app,
        language: List([language]),
      }
      if (!impressed) {
        if (videoEvent && !isMerch) {
          eventOptions.upstreamContext = upstreamContext
          dispatch(setEventVideoImpressed(eventOptions))
        }
        if (isSeries && !isMerch) {
          eventOptions.upstreamContext = upstreamContext
          dispatch(setEventSeriesImpressed(eventOptions))
        }
        if (isMerch) {
          const newContext = upstreamContext
            .set('campaignId', payload.get('id'))
            .set(typeId, contentId)
          eventOptions.upstreamContext = newContext
          if (videoEvent) {
            dispatch(setEventVideoImpressed(eventOptions))
          }
          if (isSeries) {
            dispatch(setEventSeriesImpressed(eventOptions))
          }
        }
      }
    }
    switch (pmPlacementData.get('payloadType')) {
      case PM_PLACEMENT_PAYLOAD_TYPE_FEATURED_CONTENT_IMPRESSION: {
        await getContentList({
          dispatch,
          language,
          auth,
          contentList: List([
            Map({ contentId: payload.get('id'), contentType: payload.get('type') }),
          ]),
        })
        break
      }
      case PM_PLACEMENT_PAYLOAD_TYPE_PINE_TOP_VIDEO: {
        await getContentList({
          dispatch,
          language,
          auth,
          contentList: List([
            Map({ contentId: payload.get('id'), contentType: 'video' }),
          ]),
        })
        break
      }
      case PM_PLACEMENT_PAYLOAD_TYPE_SPOTLIGHT_LIVE_ACCESS: {
        const countdown = Number(pmPlacementData.getIn(['payload', 'countdown']))
        if (_isFinite(countdown)) {
          if (_getPmPlacementTimeout) {
            clearTimeout(_getPmPlacementTimeout)
          }
          /**
           * @todo Why is this action dipatched again, the data is already loaded (Justin Morant)?
           */
          _getPmPlacementTimeout = setTimeout(() => {
            dispatch(getPmPlacementAction(placementName, language, sectionId))
            _getPmPlacementTimeout = undefined
          }, countdown + 1000)
        }
        break
      }
      default:
        break
    }
    if (isMerch) {
      await getContentList({
        dispatch,
        language,
        auth,
        contentList: pmPlacementData.getIn(['payload', 'contentItems']) || List(),
      })
    }
  } catch (e) {
    dispatch(setPmPlacementErrorAction(placementName, language, e))
  }
}

/**
 * Get content and dispatch actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.language The request language
 * @param {Map} options.auth The auth state
 * @param {List<Map<{contentId: Number, contentType: String}>[]>} options.contentList The auth state
 */
async function getContentList (options = {}) {
  const { dispatch, auth, contentList, language } = options
  const contentPromises = []
  // Set makes the values unique
  const videosList = contentList.filter(v => v.get('contentType') === 'video').toSet()
  if (!videosList.isEmpty()) {
    const ids = videosList.map(v => Number(v.get('contentId'))).toArray()
    dispatch(setMultipleVideosProcessingAction(
      ids,
      language,
      true,
    ))
    const promise = getVideoMultiple({
      dispatch,
      auth,
      videoIds: ids,
      language,
    })
    contentPromises.push(promise)
  }
  // Set makes the values unique
  const seriesList = contentList.filter(v => v.get('contentType') === 'series').toSet()
  if (!seriesList.isEmpty()) {
    const ids = seriesList.map(v => Number(v.get('contentId'))).toArray()
    dispatch(setMultipleSeriesProcessingAction(
      ids,
      language,
      true,
    ))
    const promise = getSeriesMultiple({
      dispatch,
      auth,
      seriesIds: ids,
      language,
    })
    contentPromises.push(promise)
  }
  if (_size(contentPromises) > 0) {
    await allPromise(contentPromises)
  }
}
